/**
 * 🏢 ADMIN CONSOLE SERVICE — CRUD Supabase para Backoffice
 */

import { supabase } from '../lib/supabaseClient';
import type {
  ClientLicense, LicensePlan, LicenseStatus,
  OrgDepartment, OrgSector, OrgProcess,
  ClientFeatureKey, FeatureKeyName,
  ImpersonationSession,
  AdminDashboardStats,
  AdminDashboardStatsExtended,
  AdminActivityItem,
  AuditLogEntry,
  AdminTemplate,
  OrganizationDetail,
  AdminDepartment,
  BillingSubscription,
  BillingInvoice,
  OrgIntegration,
} from '../types/adminConsole';

export class AdminConsoleService {

  // ─── Dashboard Stats ────────────────────────────────────

  static async getDashboardStats(): Promise<AdminDashboardStats> {
    const { data: licenses } = await supabase
      .from('client_licenses')
      .select('*, organizations(name)');

    const list = licenses || [];
    const now = new Date();
    const in30 = new Date();
    in30.setDate(in30.getDate() + 30);

    return {
      totalClients: list.length,
      activeClients: list.filter(l => l.status === 'active').length,
      suspendedClients: list.filter(l => l.status === 'suspended').length,
      trialClients: list.filter(l => l.status === 'trial').length,
      expiredClients: list.filter(l => l.status === 'expired').length,
      totalUsers: list.reduce((s, l) => s + (l.max_users || 0), 0),
      totalActiveUsers: list.reduce((s, l) => s + (l.active_users || 0), 0),
      mrr: list.filter(l => l.status === 'active').reduce((s, l) => s + (l.monthly_value || 0), 0),
      licensesExpiringSoon: list.filter(l => {
        if (!l.expires_at) return false;
        const exp = new Date(l.expires_at);
        return exp > now && exp <= in30;
      }).length,
    };
  }

  // ─── Licenses CRUD ──────────────────────────────────────

  static async getLicenses(): Promise<ClientLicense[]> {
    const { data, error } = await supabase
      .from('client_licenses')
      .select('*, organizations(name)')
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[AdminService] getLicenses error:', error.message);
      return [];
    }

    return (data || []).map((row: any) => ({
      ...row,
      organization_name: row.organizations?.name,
    }));
  }

  static async upsertLicense(license: Partial<ClientLicense>): Promise<ClientLicense | null> {
    const { data, error } = await supabase
      .from('client_licenses')
      .upsert(license, { onConflict: 'organization_id' })
      .select()
      .single();

    if (error) { console.warn('[AdminService] upsertLicense:', error.message); return null; }
    return data;
  }

  static async updateLicenseStatus(id: string, status: LicenseStatus): Promise<void> {
    await supabase.from('client_licenses').update({ status, updated_at: new Date().toISOString() }).eq('id', id);
  }

  // ─── Hierarchy CRUD ─────────────────────────────────────

  static async getDepartments(orgId: string): Promise<OrgDepartment[]> {
    const { data } = await supabase
      .from('org_departments')
      .select('*, org_sectors(*, org_processes(*))')
      .eq('organization_id', orgId)
      .order('sort_order');

    return (data || []).map((d: any) => ({
      ...d,
      sectors: (d.org_sectors || []).map((s: any) => ({
        ...s,
        processes: s.org_processes || [],
      })),
    }));
  }

  static async createDepartment(orgId: string, name: string): Promise<OrgDepartment | null> {
    const { data } = await supabase
      .from('org_departments')
      .insert({ organization_id: orgId, name })
      .select()
      .single();
    return data;
  }

  static async createSector(departmentId: string, name: string): Promise<OrgSector | null> {
    const { data } = await supabase
      .from('org_sectors')
      .insert({ department_id: departmentId, name })
      .select()
      .single();
    return data;
  }

  static async createProcess(sectorId: string, name: string): Promise<OrgProcess | null> {
    const { data } = await supabase
      .from('org_processes')
      .insert({ sector_id: sectorId, name })
      .select()
      .single();
    return data;
  }

  static async deleteDepartment(id: string): Promise<void> {
    await supabase.from('org_departments').delete().eq('id', id);
  }

  static async deleteSector(id: string): Promise<void> {
    await supabase.from('org_sectors').delete().eq('id', id);
  }

  static async deleteProcess(id: string): Promise<void> {
    await supabase.from('org_processes').delete().eq('id', id);
  }

  // ─── Feature Keys CRUD ──────────────────────────────────

  static async getFeatureKeys(orgId: string): Promise<ClientFeatureKey[]> {
    const { data } = await supabase
      .from('client_feature_keys')
      .select('*')
      .eq('organization_id', orgId);
    return data || [];
  }

  static async toggleFeatureKey(orgId: string, featureKey: FeatureKeyName, enabled: boolean, userId: string): Promise<void> {
    const { error } = await supabase
      .from('client_feature_keys')
      .upsert({
        organization_id: orgId,
        feature_key: featureKey,
        enabled,
        activated_at: enabled ? new Date().toISOString() : null,
        activated_by: enabled ? userId : null,
      }, { onConflict: 'organization_id,feature_key' });
    if (error) throw new Error(error.message);
  }

  // ─── Org Integrations ──────────────────────────────────

  static async getOrgIntegrations(orgId: string): Promise<OrgIntegration[]> {
    const { data, error } = await supabase
      .from('org_integrations')
      .select('*')
      .eq('organization_id', orgId);
    if (error) throw new Error(error.message);
    return (data as OrgIntegration[]) || [];
  }

  static async upsertOrgIntegration(
    orgId: string,
    integrationKey: string,
    patch: {
      enabled?: boolean;
      webhook_url?: string | null;
      webhook_secret?: string | null;
      events?: string[] | null;
      config_json?: Record<string, unknown> | null;
    },
    userId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('org_integrations')
      .upsert({
        organization_id: orgId,
        integration_key: integrationKey,
        ...patch,
        configured_at: new Date().toISOString(),
        configured_by: userId || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'organization_id,integration_key' });
    if (error) throw new Error(error.message);
  }

  // ─── Impersonation ─────────────────────────────────────

  static async startImpersonation(consultantId: string, targetOrgId: string, reason?: string): Promise<ImpersonationSession | null> {
    const { data } = await supabase
      .from('impersonation_logs')
      .insert({
        consultant_user_id: consultantId,
        target_organization_id: targetOrgId,
        reason: reason || null,
      })
      .select()
      .single();
    return data;
  }

  static async endImpersonation(sessionId: string): Promise<void> {
    await supabase
      .from('impersonation_logs')
      .update({ ended_at: new Date().toISOString() })
      .eq('id', sessionId);
  }

  static async getImpersonationLogs(): Promise<ImpersonationSession[]> {
    const { data } = await supabase
      .from('impersonation_logs')
      .select('*, organizations:target_organization_id(name)')
      .order('started_at', { ascending: false })
      .limit(50);

    return (data || []).map((row: any) => ({
      ...row,
      target_organization_name: row.organizations?.name,
    }));
  }

  // ─── Organizations list (lightweight) ───────────────────

  static async getOrganizations(): Promise<{ id: string; name: string }[]> {
    const { data } = await supabase
      .from('organizations')
      .select('id, name')
      .order('name');
    return data || [];
  }

  // ─── Organization Detail ─────────────────────────────────

  static async getOrganizationDetail(orgId: string): Promise<OrganizationDetail | null> {
    const [orgResp, licResp] = await Promise.all([
      supabase.from('organizations').select('id, name, created_at').eq('id', orgId).single(),
      supabase.from('client_licenses').select('plan, status, max_users, active_users').eq('organization_id', orgId).maybeSingle(),
    ]);
    if (orgResp.error) throw new Error(orgResp.error.message);
    if (!orgResp.data) return null;
    const org = orgResp.data as any;
    const lic = licResp.data as any;
    return {
      id: org.id,
      name: org.name,
      logo_url: org.logo_url ?? undefined,
      plan: lic?.plan ?? undefined,
      status: lic?.status ?? undefined,
      max_users: lic?.max_users ?? undefined,
      active_users: lic?.active_users ?? undefined,
      created_at: org.created_at ?? undefined,
    };
  }

  static async updateOrganization(orgId: string, fields: Partial<OrganizationDetail>): Promise<void> {
    const update: Record<string, any> = {};
    if (fields.name !== undefined) update.name = fields.name;
    if (fields.logo_url !== undefined) update.logo_url = fields.logo_url;
    if (Object.keys(update).length === 0) return;
    await supabase.from('organizations').update(update).eq('id', orgId);
  }

  // ─── Admin Departments ───────────────────────────────────

  static async getAdminDepartments(orgId: string): Promise<AdminDepartment[]> {
    const { data } = await supabase
      .from('org_departments')
      .select('id, organization_id, name, created_at')
      .eq('organization_id', orgId)
      .order('name');

    return (data || []).map((row: any) => ({
      id: row.id,
      organization_id: row.organization_id,
      name: row.name,
      manager_name: undefined,
      user_count: 0,
      project_count: 0,
      created_at: row.created_at,
    }));
  }

  // ─── Audit Logs ─────────────────────────────────────────

  static async getAuditLogs(orgId: string, limit = 100): Promise<AuditLogEntry[]> {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('[AdminService] getAuditLogs:', error.message);
      return [];
    }
    return (data || []) as AuditLogEntry[];
  }

  // ─── Templates ──────────────────────────────────────────

  static async getTemplates(orgId: string): Promise<AdminTemplate[]> {
    const { data, error } = await supabase
      .from('process_blueprints_with_latest_version')
      .select('id, organization_id, name, source_type, status, version_semver, created_by, created_at, updated_at')
      .eq('organization_id', orgId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.warn('[AdminService] getTemplates:', error.message);
      return [];
    }
    return (data || []).map((row: any) => ({
      id: row.id,
      organization_id: row.organization_id,
      title: row.name,
      category: row.source_type,
      type: 'workflow' as const,
      status: row.status,
      version: row.version_semver || '1.0.0',
      created_by: row.created_by,
      created_at: row.created_at,
      updated_at: row.updated_at,
    }));
  }

  // ─── Extended Dashboard Stats ────────────────────────────

  static async getDashboardStatsExtended(): Promise<AdminDashboardStatsExtended> {
    const base = await AdminConsoleService.getDashboardStats();

    // Recent activity from audit_logs if available
    let recentActivity: AdminActivityItem[] = [];
    try {
      const { data } = await supabase
        .from('audit_logs')
        .select('id, user_id, action, metadata, created_at')
        .order('created_at', { ascending: false })
        .limit(10);
      recentActivity = (data || []).map((row: any) => ({
        id: row.id,
        user_name: row.metadata?.user_name || 'Usuário',
        action: row.action || 'Ação',
        resource: row.metadata?.resource_type || row.metadata?.resource || '',
        created_at: row.created_at,
      }));
    } catch {
      // audit_logs may not exist yet
    }

    return {
      ...base,
      totalWorkflows: 0,
      apiRequests: 0,
      activeOrganizations: base.activeClients,
      recentActivity,
    };
  }

  // ─── Billing ────────────────────────────────────────────

  static async getBillingSubscriptions(): Promise<BillingSubscription[]> {
    const { data } = await supabase
      .from('client_licenses')
      .select('*, organizations(name)')
      .order('created_at', { ascending: false });

    return (data || []).map((row: any) => ({
      id: row.id,
      organization_id: row.organization_id,
      organization_name: row.organizations?.name,
      plan: row.plan,
      billing_cycle: 'monthly' as const,
      monthly_value: row.monthly_value || 0,
      status: row.status,
      next_billing_date: null,
      trial_ends_at: row.trial_ends_at ?? null,
      started_at: row.started_at || row.created_at,
    }));
  }

  static async getBillingInvoices(): Promise<BillingInvoice[]> {
    try {
      const { data } = await supabase
        .from('invoices')
        .select('id, workspace_id, amount, status, issued_at, paid_at, due_at, currency, created_at')
        .order('issued_at', { ascending: false })
        .limit(50);
      return (data || []).map((row: any) => ({
        id: row.id,
        organization_id: row.workspace_id,
        organization_name: undefined,
        amount: row.amount || 0,
        status: row.status || 'pending',
        issued_at: row.issued_at || row.created_at,
        paid_at: row.paid_at ?? null,
        due_date: row.due_at || row.issued_at,
      }));
    } catch {
      return [];
    }
  }

  // ─── User Management ────────────────────────────────────

  static async updateUserRole(organizationId: string, userId: string, newRole: string): Promise<void> {
    const { error } = await supabase
      .from('user_organizations')
      .update({ role: newRole })
      .eq('organization_id', organizationId)
      .eq('user_id', userId);
    if (error) throw error;
  }

  static async removeUserFromOrg(organizationId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_organizations')
      .delete()
      .eq('organization_id', organizationId)
      .eq('user_id', userId);
    if (error) throw error;
  }
}
